import html2canvas from "html2canvas";
import { addLog } from "./add-log.utility";

export const downloadElementAsImage = (
    element: HTMLElement | null,
    width: number,
    height: number,
    fileName: string = "infographic",
    scale: number = 3,
): Promise<void> => {
    return new Promise<void>(async (resolve, reject) => {
        if (!element) return reject(new Error("Element is null or undefined"));

        try {
            const canvas = await html2canvas(element, {
                width: width,
                height: height,
                scale: scale,
                useCORS: true,
                allowTaint: true,
            } as any);

            const image = canvas.toDataURL("image/png");
            const link = document.createElement("a");
            link.href = image;
            link.download = fileName + '.png';
            link.click();

            resolve();
        } catch (error) {
            reject(error);
        }
    });
};

export const getElementAsBlob = (
    infoId: string,
    element: HTMLElement | null,
    width: number,
    height: number,
    type: 'jpeg' | 'png' = "png",
    scale: number = 3
): Promise<Blob> => {
    return new Promise<Blob>((resolve, reject) => {
        if (!element) {
            addGetElementAsBlobLog(infoId, "", "Element is null or undefined");
            return reject(new Error("Element is null or undefined"));
        }

        html2canvas(element, {
            width,
            height,
            scale,
            useCORS: true,
            allowTaint: true,
        } as any)
            .then((canvas) => {
                canvas.toBlob((blob) => {
                    if (blob) {
                        addGetElementAsBlobLog(infoId, element.outerHTML);
                        resolve(blob);
                    } else {
                        addGetElementAsBlobLog(infoId, element.outerHTML, "Failed to convert canvas to blob");
                        reject(new Error("Failed to convert canvas to blob"));
                    }
                }, `image/${type}`, 1.0);
            })
            .catch((error) => {
                addGetElementAsBlobLog(infoId, element.outerHTML, error);
                reject(error);
            });
    });
};

export const optimizeImage = (file: File | Blob): Promise<Blob | File> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();

        reader.onload = (e) => {
            if (typeof e.target?.result === "string") {
                img.src = e.target.result;
            } else {
                reject("Failed to read file");
            }
        };

        reader.onerror = () => reject("Error reading the file");
        reader.readAsDataURL(file);

        img.onload = () => {
            const maxDimension = 1920;
            let { width, height } = img;

            // If the image is already within the maxDimension, return the original file
            if (width <= maxDimension && height <= maxDimension) {
                resolve(file);
                return;
            }

            const scale = Math.min(maxDimension / width, maxDimension / height);
            width = Math.floor(width * scale);
            height = Math.floor(height * scale);

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            if (!ctx) {
                reject("Canvas context is not available");
                return;
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject("Failed to create Blob from canvas");
                    }
                },
                file.type || "image/jpeg"
            );
        };

        img.onerror = () => reject("Image loading failed.");
    });
};

export const getCroppedImg = (imageSrc: string, crop: any): Promise<{ file: Blob, src: string }> => {
    return new Promise((resolve) => {
        const image = new Image();
        image.src = imageSrc;
        image.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            if (!ctx) return;

            canvas.width = crop.width;
            canvas.height = crop.height;

            ctx.drawImage(
                image,
                crop.x,
                crop.y,
                crop.width,
                crop.height,
                0,
                0,
                canvas.width,
                canvas.height
            );

            canvas.toBlob((blob) => {
                if (blob) {
                    const fileUrl = URL.createObjectURL(blob);
                    resolve({ file: blob, src: fileUrl });
                }
            }, "image/png");
        };
    });
};

const addGetElementAsBlobLog = (infoId: string, html: string, error = "") => {
    addLog("CAPTURE_IMAGE_INFO", { infoId, html, error });
}